import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { ApiError, AuthError } from "@/lib/api-errors"

interface Household {
  id: string
  name: string
  currency: string
  locale: string
  monthStartDay: number
  ownerLabels: Record<string, string>
}

interface HouseholdMember {
  id: string
  email: string
  name: string | null
  owner: string | null
}

interface HouseholdData {
  success: boolean
  household: Household
  members: HouseholdMember[]
  isOwner?: boolean
}

interface Invite {
  id: string
  invitedEmail: string
  token: string
  expiresAt: string
  acceptedAt: string | null
}

export function useHousehold() {
  return useQuery({
    queryKey: ["household"],
    queryFn: () => api.get<HouseholdData>("/households"),
    // 404 = no household yet (the onboarding gate's signal), so don't retry it;
    // retry transient failures (429, 5xx, offline) a couple of times first.
    retry: (count, err) =>
      count < 2 &&
      !(err instanceof AuthError) &&
      !(err instanceof ApiError && err.status < 500 && err.status !== 429),
  })
}

export function useHouseholdInvites() {
  return useQuery({
    queryKey: ["household", "invites"],
    queryFn: () => api.get<{ success: boolean; data: Invite[] }>("/households/invites"),
  })
}

export function useUpdateHousehold() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name?: string
      currency?: string
      locale?: string
      ownerLabels?: Record<string, string>
    }) => api.patch<{ success: boolean; household: Household }>("/households", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household"] }),
  })
}

export function useCreateHousehold() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name?: string }) =>
      api.post<{ success: boolean; household: Household }>("/households", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household"] }),
  })
}

export function useAcceptInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => api.post("/households/invites/accept", { token }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household"] }),
  })
}

export function useCreateInvite() {
  const qc = useQueryClient()
  return useMutation({
    // owner: the slot the invitee takes on accept ("Invite as"); omit for the first free one.
    mutationFn: ({ email, owner }: { email: string; owner?: string }) =>
      api.post<{ success: boolean; invite: Invite; inviteUrl: string }>("/households/invites", {
        email,
        ...(owner ? { owner } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household", "invites"] }),
  })
}

export function useRevokeInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/households/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household", "invites"] }),
  })
}
